import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone, AlertTriangle, Wrench, Info, X } from 'lucide-react';
import { ANNOUNCEMENT_VARIANT_STYLES } from '../constants/announcements';

const ICON_BY_VARIANT = {
  info: Info,
  warning: Megaphone,
  critical: AlertTriangle,
  maintenance: Wrench,
};

/**
 * Render-only announcement banner. Mirrors PromoBanner's DOM and palette so
 * the visual position and styling stay identical when an announcement
 * overrides the promo banner.
 *
 * State (subscription, dismissal, schedule, priority) lives in
 * useActiveAnnouncement; this component is intentionally dumb so it can
 * also be reused as a live preview in the admin editor.
 */
export default function AnnouncementBanner({ announcement, onDismiss, isPreview = false }) {
  const navigate = useNavigate();
  if (!announcement) return null;

  const styles = ANNOUNCEMENT_VARIANT_STYLES[announcement.variant] || ANNOUNCEMENT_VARIANT_STYLES.info;
  const Icon = ICON_BY_VARIANT[announcement.variant] || Megaphone;

  const handleCta = () => {
    if (isPreview) return;
    const url = announcement.ctaUrl;
    if (!url) return;
    if (/^https?:\/\//i.test(url)) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };

  return (
    <div
      role="region"
      aria-label="System announcement"
      className={`relative w-full ${styles.bar} shadow-sm`}
    >
      <div className="flex items-center gap-3 px-3 sm:px-5 py-2 sm:py-2.5 max-w-screen-2xl mx-auto">
        <Icon size={16} className="shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm">
          {announcement.eyebrow ? (
            <span className="font-mono uppercase tracking-[0.12em] text-[10px] sm:text-[11px] font-semibold opacity-80 shrink-0">
              {announcement.eyebrow}
            </span>
          ) : null}
          <span className="font-medium leading-snug">{announcement.message}</span>
        </div>
        {announcement.ctaLabel && announcement.ctaUrl ? (
          <button
            type="button"
            onClick={handleCta}
            className={`shrink-0 rounded-md px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${styles.cta}`}
          >
            {announcement.ctaLabel}
          </button>
        ) : null}
        {announcement.dismissible && onDismiss ? (
          <button
            type="button"
            onClick={() => onDismiss(announcement.id, announcement.version)}
            aria-label="Dismiss announcement"
            className={`shrink-0 p-1 rounded transition-colors ${styles.dismiss}`}
          >
            <X size={16} />
          </button>
        ) : null}
      </div>
    </div>
  );
}
